import { LinearProgress } from "@mui/material";
import { type FC, Fragment, type ReactNode, useMemo } from "react";
import { NotSet } from "../components/not-set";
import { SmsBubbleComponent } from "../components/sms-bubble";
import { useBuiltTwilioMessage } from "../hooks/use-built-twilio-message";

const LINK_REGEX = new RegExp(/(https?:\/\/[^\s/$.?#].[^\s]*)/g);

const CELinkText: FC<{ section: string }> = ({ section }) => {
  // eslint-disable-next-line react-hooks/purity
  const randomString = useMemo(() => Math.random().toString(36).substring(2, 9), []);
  const url = `${section}#${randomString}`; // forces browser to treat it as a new link every preview open.
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      https://ce-l.ink/{randomString}
    </a>
  );
};

const PreviewBodyTwilioContentLine: FC<{ line: string }> = ({ line }) => {
  // Twilio shortens every link to a ce-l.ink tracking url when the message is sent, so render
  // all links (survey, content library, email communication, plain url) as short-url anchors,
  // and the rest as plain text in a paragraph.
  const elements: ReactNode[] = [];

  line.split(LINK_REGEX)?.forEach((section, index) => {
    if (section.match(LINK_REGEX)) {
      elements.push(
        <Fragment key={index}>
          <CELinkText section={section} />{" "}
        </Fragment>,
      );
    } else if (section === "") {
      // can be end of line or for paragraph spacing
      // zero-width space to maintain line breaks
      elements.push(<Fragment key={index}>&#8288;</Fragment>);
    } else {
      elements.push(<span key={index}>{section}</span>);
    }
  });

  return <p style={{ margin: 0 }}>{elements.map((element) => element)}</p>;
};

export const PreviewBodyTwilioContent: FC = () => {
  const { data, loading } = useBuiltTwilioMessage();

  if (loading) return <LinearProgress />;

  if (!data) return <NotSet />;

  return (
    <div className="twilio-preview-capture-area">
      <SmsBubbleComponent>
        {data.split("\n").map((line, index) => (
          <PreviewBodyTwilioContentLine key={`line-${index}`} line={line} />
        ))}
      </SmsBubbleComponent>
    </div>
  );
};
